import { useEffect, useState, useSyncExternalStore } from 'react';
import { useAccessToken } from '../auth/use-access-token.js';
import { apiFetch } from './client.js';

/**
 * The MPC coach's Book (stage B; canvas row 10, approved 16 October): who the
 * lesson is for, found from everyone in the system, and booking them.
 *
 * WHO IT IS FOR IS HELD BY THE APP, not the address: a child's name does not
 * belong in a URL. Kept for the browser tab's session (sessionStorage), so a
 * reload on the hour screen still knows; gone when the tab closes.
 */

type GetToken = () => Promise<string | null>;

export interface AthleteMatch {
  client_id: string;
  name: string;
  /** Null for an athlete a coach added whose parent has not given it yet (stage C). */
  birth_year: number | null;
  /** Null for the account holder: nothing under their name (board A3). */
  parent_name: string | null;
  adult_account_id: string;
  /** Decides the lessons and prices the family can buy, as for a parent. */
  country: string | null;
}

/**
 * Somebody not yet in the system (stage C; canvas row 11): the athlete's name
 * and the parent's name, phone and email, as the coach typed them - phone and
 * email may be empty (ruled 23 October). Booking them starts the family and
 * invites the parent, by email when there is one.
 */
export interface NewAthlete {
  new: true;
  name: string;
  athlete_first_name: string;
  athlete_last_name: string;
  parent_first_name: string;
  parent_last_name: string;
  /** Absent on a choice kept from before 23 October. */
  parent_phone?: string;
  parent_email: string;
}

export type ChosenAthlete = AthleteMatch | NewAthlete;

export function isNewAthlete(athlete: ChosenAthlete): athlete is NewAthlete {
  return 'new' in athlete;
}

const KEY = 'coachtools.booking-for';
const listeners = new Set<() => void>();
let chosen: ChosenAthlete | null = read();

function read(): ChosenAthlete | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as ChosenAthlete) : null;
  } catch {
    return null;
  }
}

export function chooseAthlete(next: ChosenAthlete | null): void {
  chosen = next;
  try {
    if (next) window.sessionStorage.setItem(KEY, JSON.stringify(next));
    else window.sessionStorage.removeItem(KEY);
  } catch {
    // Storage off: it still holds for this page, and asks again after a reload.
  }
  listeners.forEach((listener) => listener());
}

export function useChosenAthlete(): ChosenAthlete | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => chosen,
  );
}

/** Matches for what has been typed, a moment after typing stops. */
export function useAthleteSearch(query: string): { matches: AthleteMatch[]; searching: boolean } {
  const getToken = useAccessToken();
  const [matches, setMatches] = useState<AthleteMatch[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const text = query.trim();
    if (!text) {
      setMatches([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const timer = window.setTimeout(() => {
      apiFetch<{ athletes: AthleteMatch[] }>(`/api/coach/athletes?q=${encodeURIComponent(text)}`, getToken)
        .then((body) => {
          if (!cancelled) setMatches(body.athletes);
        })
        .catch(() => {
          if (!cancelled) setMatches([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, getToken]);

  return { matches, searching };
}

export function coachBook(
  getToken: GetToken,
  body: { slot_id: string; service_id: string; capacity_tier_id: string } & (
    | { athlete_client_id: string }
    | { new_athlete: Omit<NewAthlete, 'new' | 'name'> }
  ),
): Promise<{
  booking_id: string;
  slot_start: string;
  athlete_client_id: string;
  invited_email: string | null;
  adult_account_id: string;
  /** No address to email the invitation to: Booked offers Share link. */
  share_link: boolean;
}> {
  return apiFetch('/api/coach/bookings', getToken, { method: 'POST', body: JSON.stringify(body) });
}
